/*
  <velune-lazy-video> — editorial background-style video, shared by the home video section and the PDP stories.
  - Nothing heavy loads with the page: the file is attached only when the element comes near the viewport.
  - Plays muted / inline / looped while visible, pauses when it leaves.
  - Reduced motion or autoplay refused → the poster stays and the quiet toggle button lets the visitor play it.
  Markup: <velune-lazy-video data-src-small data-src-large> <img poster> <video muted playsinline loop preload="none">
          <button data-video-toggle hidden>…</button> </velune-lazy-video>
*/
class VeluneLazyVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.toggle = this.querySelector('[data-video-toggle]');
    if (!this.video) return;
    this.userPaused = false;
    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.setupSkips();

    this.video.addEventListener('playing', () => {
      this.classList.add('is-playing');
      this.setToggle(true);
    });
    this.video.addEventListener('pause', () => this.setToggle(false));
    if (this.toggle) {
      this.toggle.addEventListener('click', () => {
        this.attach();
        if (this.video.paused) {
          this.userPaused = false;
          this.play();
        } else {
          this.userPaused = true;
          this.video.pause();
        }
      });
    }

    if (!('IntersectionObserver' in window)) {
      this.attach();
      this.showToggle();
      return;
    }

    this.loadObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.loadObserver.disconnect();
        this.attach();
      },
      { rootMargin: '400px 0px' }
    );
    this.loadObserver.observe(this);

    this.playObserver = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        if (visible) {
          if (this.reduce || this.userPaused) {
            this.showToggle();
            return;
          }
          this.play();
        } else if (!this.video.paused) {
          this.video.pause();
        }
      },
      { threshold: 0.35 }
    );
    this.playObserver.observe(this);
  }

  /*
    data-skip="24.4-28, …" (seconds): passages that are never shown (e.g. a colour we don't sell).
    The file itself is untouched. Checked on every presented frame (requestVideoFrameCallback) so the
    jump happens before a skipped frame is painted; falls back to timeupdate + a short interval.
  */
  setupSkips() {
    this.skips = (this.dataset.skip || '')
      .split(',')
      .map((range) => range.split('-').map((value) => parseFloat(value.trim().replace(',', '.'))))
      .filter((pair) => pair.length === 2 && pair[0] >= 0 && pair[1] > pair[0]);
    if (!this.skips.length) return;

    const check = (time) => {
      for (const [from, to] of this.skips) {
        if (time >= from && time < to) {
          this.video.currentTime = to;
          return;
        }
      }
    };

    // Frame-accurate path: look one frame ahead so the first skipped frame is never painted.
    if ('requestVideoFrameCallback' in HTMLVideoElement.prototype) {
      const onFrame = (_now, meta) => {
        check(meta.mediaTime + 1 / 30);
        this.video.requestVideoFrameCallback(onFrame);
      };
      this.video.requestVideoFrameCallback(onFrame);
    }

    // Safety net (always on): browsers that lack frame callbacks or don't paint (background tab).
    this.video.addEventListener('timeupdate', () => check(this.video.currentTime + 0.1));
    this.video.addEventListener('seeked', () => check(this.video.currentTime));
    this.video.addEventListener('playing', () => {
      clearInterval(this.skipTimer);
      this.skipTimer = setInterval(() => check(this.video.currentTime + 0.06), 40);
    });
    this.video.addEventListener('pause', () => clearInterval(this.skipTimer));
  }

  disconnectedCallback() {
    clearInterval(this.skipTimer);
    if (this.loadObserver) this.loadObserver.disconnect();
    if (this.playObserver) this.playObserver.disconnect();
  }

  attach() {
    if (this.attached) return;
    this.attached = true;
    const wide = window.innerWidth * (window.devicePixelRatio || 1) > 900;
    const src = (wide ? this.dataset.srcLarge : this.dataset.srcSmall) || this.dataset.srcLarge;
    if (!src) return;
    this.video.src = src;
    this.video.preload = 'auto';
  }

  play() {
    this.attach();
    const attempt = this.video.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(() => this.showToggle());
    this.showToggle();
  }

  showToggle() {
    if (this.toggle) this.toggle.hidden = false;
  }

  setToggle(playing) {
    if (!this.toggle) return;
    this.toggle.classList.toggle('is-playing', playing);
    this.toggle.setAttribute('aria-label', playing ? 'Mettre la vidéo en pause' : 'Lire la vidéo');
  }
}

if (!customElements.get('velune-lazy-video')) customElements.define('velune-lazy-video', VeluneLazyVideo);
